import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { caseStudies } from '../content/data';

const CaseStudies = () => {
  const [expandedStudy, setExpandedStudy] = useState<string | null>(null);

  const toggleExpanded = (studyId: string) => {
    setExpandedStudy(expandedStudy === studyId ? null : studyId);
  };

  return (
    <section id="case-studies" className="section-padding">
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-dm-sans font-bold text-text-slate dark:text-surface-white mb-4">
            Featured Projects
          </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Swiss franc and international stablecoin work, with a clear view of the project, my role, and what I was responsible for.
            </p>
        </motion.div>

        <div className="space-y-8">
          {caseStudies.map((study, index) => (
            <motion.div
              key={study.id}
              id={`case-study-${study.id}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="card"
            >
              <div className="space-y-6">
                {/* Header */}
                <div>
                  <h3 className="text-xl sm:text-2xl font-semibold text-text-slate dark:text-surface-white mb-1">
                    {study.project}
                  </h3>
                  <p className="text-accent-crimson font-medium mb-4">
                    {study.role}
                  </p>
                </div>

                {/* Problem & Solution */}
                <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <h4 className="text-base sm:text-lg font-medium text-text-slate dark:text-surface-white mb-2">
                      Challenge
                    </h4>
                    <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
                      {study.problem}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-base sm:text-lg font-medium text-text-slate dark:text-surface-white mb-2">
                      Solution
                    </h4>
                    <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
                      {study.solution}
                    </p>
                  </div>
                </div>

                {/* Impact Metrics */}
                <div>
                  <h4 className="text-base sm:text-lg font-medium text-text-slate dark:text-surface-white mb-4">
                    Impact
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {study.impact.map((metric, metricIndex) => (
                      <div key={metricIndex} className="p-3 sm:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-accent-crimson/20 shadow-sm">
                        <div className="text-sm sm:text-base font-semibold text-text-slate dark:text-surface-white mb-1">
                          {metric.metric}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                          {metric.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Read More Section */}
                {study.details && (
                  <div>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => toggleExpanded(study.id)}
                      className="flex items-center space-x-2 text-accent-crimson font-medium hover:text-accent-crimson/80 transition-colors"
                    >
                      <span>Read more</span>
                      {expandedStudy === study.id ? (
                        <ChevronUp size={16} />
                      ) : (
                        <ChevronDown size={16} />
                      )}
                    </motion.button>

                    <AnimatePresence>
                      {expandedStudy === study.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <p className="text-gray-600 dark:text-gray-300">
                              {study.details}
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
